import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Send, Mail, MapPin, Calendar, ExternalLink, Globe } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ContactSection = () => {
  const [form, setForm] = useState({ name: "", email: "", company: "", product: "", message: "" });
  const [demoProduct, setDemoProduct] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const subject = encodeURIComponent(`Kontaktanfrage – ${form.product || "Allgemein"}`);
    const body = encodeURIComponent(
      `Name: ${form.name}\nE-Mail: ${form.email}\nUnternehmen: ${form.company || "–"}\nProdukt: ${form.product || "–"}\n\nNachricht:\n${form.message}`
    );
    window.location.href = `mailto:hallo@successio.de?subject=${subject}&body=${body}`;
  };

  return (
    <section id="kontakt" className="py-16 md:py-20 relative overflow-hidden">
      <div className="absolute inset-0 bg-muted/30" />
      <div className="absolute top-0 left-0 right-0 h-16 section-gradient-top" />
      <div className="absolute bottom-0 left-0 right-0 h-16 section-gradient-bottom" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-10"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            Kontakt aufnehmen
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Schreiben Sie uns – wir melden uns zeitnah bei Ihnen
          </p>
        </motion.div>

        <motion.div
          className="grid lg:grid-cols-2 gap-8"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          {/* Demo Card - Links */}
          <Card className="border-border shadow-sm transition-all duration-300 hover:shadow-md hover:border-primary/20">
            <CardContent className="p-8 flex flex-col h-full">
              <Calendar className="w-8 h-8 text-primary/60 mb-3" />
              <h3 className="text-xl font-bold text-foreground mb-2">Demo vereinbaren</h3>
              <p className="text-sm text-muted-foreground mb-4">Vereinbaren Sie eine persönliche Demo und lernen Sie unsere Produkte kennen.</p>
              <div className="mb-4">
                <Label>Produkt</Label>
                <Select value={demoProduct} onValueChange={setDemoProduct}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Bitte wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="recruTen.AI">recruTen.AI</SelectItem>
                    <SelectItem value="zenTen.AI">zenTen.AI</SelectItem>
                    <SelectItem value="Beide Produkte">Beide Produkte</SelectItem>
                    <SelectItem value="Sonstiges">Sonstiges</SelectItem>
                  </SelectContent>
              </Select>
              <span className="text-xs text-muted-foreground mt-2">Kostenlos & unverbindlich · Termin sofort buchbar</span>
              </div>
              <div className="mt-auto">
                <Button
                  asChild
                  className="w-full rounded-full font-semibold bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 shadow-lg shadow-primary/20"
                  size="lg"
                >
                  <a href={`https://calendly.com/christoph-tegeler-tpfcompany/kennenlernen-mit-christoph-tegeler${demoProduct ? `?a1=${encodeURIComponent(demoProduct)}` : ''}`} target="_blank" rel="noopener noreferrer">
                    <Calendar className="mr-2 w-4 h-4" /> Demo vereinbaren <ExternalLink className="ml-2 w-3 h-3" />
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Kontaktformular - Rechts */}
          <Card className="border-border shadow-sm transition-all duration-300 hover:shadow-md hover:border-primary/20">
            <CardContent className="p-8 flex flex-col h-full">
              <h3 className="text-xl font-bold text-foreground mb-6">Kontaktformular</h3>
              <form onSubmit={handleSubmit} className="space-y-4 flex flex-col flex-1">
                <div>
                  <Label htmlFor="name">Name *</Label>
                  <Input id="name" required maxLength={100} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ihr Name" className="mt-1" />
                </div>
                <div>
                  <Label htmlFor="email">E-Mail *</Label>
                  <Input id="email" type="email" required maxLength={255} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="ihre@email.de" className="mt-1" />
                </div>
                <div>
                  <Label htmlFor="company">Unternehmen</Label>
                  <Input id="company" maxLength={100} value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Ihr Unternehmen" className="mt-1" />
                </div>
                <div>
                  <Label>Produkt</Label>
                  <Select value={form.product} onValueChange={(val) => setForm({ ...form, product: val })}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Bitte wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="recruTen.AI">recruTen.AI</SelectItem>
                      <SelectItem value="zenTen.AI">zenTen.AI</SelectItem>
                      <SelectItem value="Beide Produkte">Beide Produkte</SelectItem>
                      <SelectItem value="Sonstiges">Sonstiges</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="message">Nachricht *</Label>
                  <Textarea id="message" required maxLength={1000} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Wie können wir Ihnen helfen?" rows={3} className="mt-1" />
                </div>
                <Button type="submit" className="w-full rounded-full font-semibold mt-auto" size="lg">
                  <Send className="mr-2 w-4 h-4" /> Nachricht senden
                </Button>
              </form>
            </CardContent>
          </Card>
        </motion.div>

        <div className="mt-8 flex flex-wrap justify-center gap-6">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Mail className="w-4 h-4" />
            <span className="text-sm">hallo@successio.de</span>
          </div>
          <div className="flex items-center gap-2 text-muted-foreground">
            <Globe className="w-4 h-4" />
            <a href="https://successio.de" target="_blank" rel="noopener noreferrer" className="text-sm hover:text-foreground transition-colors">successio.de</a>
          </div>
          <div className="flex items-center gap-2 text-muted-foreground">
            <MapPin className="w-4 h-4" />
            <span className="text-sm">Successio Unternehmensnachfolge GmbH, Deutschland</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
